import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Select, Button, Space, Card, Tag, Row, Col, Typography, message, Popconfirm,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, EyeOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import useAuthStore from '../store/authStore';
import EmployeeDrawer from '../components/EmployeeDrawer';
import IndividualFormModal from '../components/IndividualFormModal';
import { WARNING_BG, WARNING_COLOR, daysUntilBirthday, pluralDays, isBirthdaySoon } from '../utils/birthday';

const { Title } = Typography;
const { Option } = Select;

interface Department {
  id: number;
  name: string;
}

interface Position {
  id: number;
  name: string;
}

const EmployeeList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, user } = useAuthStore();
  const canWrite = hasPermission('employees:write');
  const canDelete = hasPermission('employees:delete');

  const [employees, setEmployees] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [drawerEmployee, setDrawerEmployee] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [indModalOpen, setIndModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState<number | undefined>();
  const [organization, setOrganization] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>('active');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, per_page: perPage };
      if (search) params.search = search;
      if (departmentId) params.department_id = departmentId;
      if (organization) params.organization = organization;
      if (statusFilter) params.status = statusFilter;

      const res = await api.get('/employees', { params });
      setEmployees(res.data.items);
      setTotal(res.data.total);
    } catch {
      message.error('Ошибка загрузки сотрудников');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, departmentId, organization, statusFilter]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    api.get('/departments').then((res) => setDepartments(res.data));
    api.get('/positions').then((res) => setPositions(res.data));
  }, []);

  // Поддержка ?highlight=<id> — открыть drawer сотрудника (из уведомлений о ДР и т.п.)
  useEffect(() => {
    const hl = searchParams.get('highlight');
    if (!hl) return;
    const id = parseInt(hl, 10);
    if (isNaN(id)) return;
    api.get(`/employees/${id}`).then((res) => {
      setDrawerEmployee(res.data);
      setDrawerOpen(true);
      // Убираем query чтобы не переоткрывалось при навигации
      const next = new URLSearchParams(searchParams);
      next.delete('highlight');
      setSearchParams(next, { replace: true });
    }).catch(() => {});
  }, [searchParams, setSearchParams]);

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/employees/${id}`);
      message.success('Сотрудник уволен');
      fetchEmployees();
    } catch {
      message.error('Ошибка удаления');
    }
  };

  const role = user?.role;
  const isInfo = role === 'info';
  const isOilManager = role === 'oil_manager';
  const isFullView = !isInfo && !isOilManager;

  const columns: any[] = [];

  if (isFullView) {
    columns.push({
      title: 'Таб. №',
      dataIndex: 'tab_number',
      key: 'tab_number',
      width: 90,
    });
  }

  columns.push(
    {
      title: 'ФИО',
      key: 'fio',
      render: (_: any, rec: any) =>
        `${rec.last_name} ${rec.first_name}${rec.middle_name ? ' ' + rec.middle_name : ''}`,
      sorter: (a: any, b: any) => a.last_name.localeCompare(b.last_name),
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Подразделение',
      key: 'department',
      render: (_: any, rec: any) => rec.department?.name || '—',
    },
    {
      title: 'Должность',
      key: 'position',
      render: (_: any, rec: any) => rec.position?.name || '—',
    },
  );

  if (isOilManager) {
    columns.push({
      title: 'Дата рождения',
      dataIndex: 'birth_date',
      key: 'birth_date',
      render: (v: string | null) => {
        if (!v) return '—';
        const date = new Date(v).toLocaleDateString('ru');
        if (!isBirthdaySoon(v)) return date;
        const days = daysUntilBirthday(v);
        const label = days === 0 ? 'Сегодня! 🎂' : `через ${days} ${pluralDays(days)}`;
        return (
          <Space size={4}>
            <span>{date}</span>
            <Tag color="gold" style={{ margin: 0 }}>{label}</Tag>
          </Space>
        );
      },
    });
  }

  if (isFullView) {
    columns.push({
      title: 'Организация',
      dataIndex: 'organization',
      key: 'organization',
      render: (v: string) => (
        <Tag color={v === 'ООО АвтоРегиум' ? 'blue' : 'green'}>
          {v === 'ООО АвтоРегиум' ? 'АвтоРегиум' : 'ИП Фомин'}
        </Tag>
      ),
    });
  }

  if (isFullView || isOilManager) {
    columns.push({
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => {
        const map: Record<string, { color: string; label: string }> = {
          active: { color: 'green', label: 'Работает' },
          on_leave: { color: 'orange', label: 'В отпуске' },
          sick: { color: 'gold', label: 'На больничном' },
          dismissed: { color: 'red', label: 'Уволен' },
        };
        const s = map[v] || { color: 'default', label: v };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    });
  }

  columns.push({
    title: 'Действия',
    key: 'actions',
    width: 120,
    render: (_: any, rec: any) => (
      <Space>
        {canWrite && (
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/employees/${rec.id}/edit`)}
          />
        )}
        {canDelete && rec.status !== 'dismissed' && (
          <Popconfirm
            title="Уволить сотрудника?"
            onConfirm={() => handleDelete(rec.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        )}
      </Space>
    ),
  });

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Сотрудники</Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchEmployees}>
              Обновить
            </Button>
            {canWrite && (
              <>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/employees/new')}>
                  Добавить
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => setIndModalOpen(true)}>
                  + Физлицо
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Поиск по ФИО, телефону..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Подразделение"
              allowClear
              style={{ width: '100%' }}
              value={departmentId}
              onChange={(v) => { setDepartmentId(v); setPage(1); }}
            >
              {departments.map((d) => (
                <Option key={d.id} value={d.id}>{d.name}</Option>
              ))}
            </Select>
          </Col>
          {isFullView && (
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Организация"
                allowClear
                style={{ width: '100%' }}
                value={organization}
                onChange={(v) => { setOrganization(v); setPage(1); }}
              >
                <Option value="ООО АвтоРегиум">ООО АвтоРегиум</Option>
                <Option value="ИП Фомин Д.А.">ИП Фомин Д.А.</Option>
              </Select>
            </Col>
          )}
          {(isFullView || isOilManager) && (
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Статус"
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={(v) => { setStatusFilter(v); setPage(1); }}
              >
                <Option value="active">Работает</Option>
                <Option value="on_leave">В отпуске</Option>
                <Option value="sick">На больничном</Option>
                <Option value="dismissed">Уволен</Option>
              </Select>
            </Col>
          )}
        </Row>
      </Card>

      <Table
        dataSource={employees}
        columns={columns}
        rowKey="id"
        loading={loading}
        onRow={(record) => {
          const bdSoon = record.birth_date && isBirthdaySoon(record.birth_date);
          return {
            onClick: () => {
              setDrawerEmployee(record);
              setDrawerOpen(true);
            },
            style: {
              cursor: 'pointer',
              ...(bdSoon ? { background: WARNING_BG, borderLeft: `3px solid ${WARNING_COLOR}` } : {}),
            },
          };
        }}
        pagination={{
          current: page,
          pageSize: perPage,
          total,
          onChange: (p) => setPage(p),
          showTotal: (t) => `Всего: ${t}`,
          showSizeChanger: false,
        }}
        size="middle"
        scroll={{ x: 800 }}
      />

      <EmployeeDrawer
        employee={drawerEmployee}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <IndividualFormModal
        open={indModalOpen}
        onClose={() => setIndModalOpen(false)}
        defaultRole="employee"
      />
    </div>
  );
};

export default EmployeeList;
